import React, { useEffect } from 'react'
import { Box, Button, Flex, FormControl, Image, Link, VStack } from '@chakra-ui/react'
import BlackLogo from '../../assets/icons/blackLogo.svg'
import { ChevronLeftIcon } from '@chakra-ui/icons'
import FormInput from '../../components/Input'
import { useFormik } from 'formik'
import { waitlistSchema } from '../../validations/waitlist'
import DefaultButton from '../../components/Button/Button'
import { endpoints, postFunc } from '../../api'
import Metadata from '../../components/Metadata'

  const waitlistMetadata = {
    title: 'Waitlist - Safelto',
    description: 'Join the waitlist for Safelto as a community manager. Be the first to know when we start operating.',
    keywords: ['waitlist', 'safelto waitlist', 'community manager waitlist', 'property management waitlist'],
    image: '/assets/images/safelto-big.png',
    url: 'https://safelto.com/waitlist',
    type: 'website' as 'website'
  }

  const WaitingListPage = () => {
    const [initialValues] = React.useState({ name: '', email: '' })
    const [loading, setLoading] = React.useState(false)
    const [isModalOpen, setModalOpen] = React.useState(false)
    const [submittedEmail, setSubmittedEmail] = React.useState<string | null>(null)

    const formik = useFormik({
      initialValues,
      validationSchema: waitlistSchema,
      onSubmit: (data) => {
        if (loading) return

        setLoading(true)
        setSubmittedEmail(data.email)
        postFunc(endpoints.waitlist, data)
          .then(() => {
            // Open modal on successful submission
            setModalOpen(true)
            formik.resetForm()
          })
          .catch((e) => {
            if (e.statusCode === 409) {
              formik.setErrors({ email: 'Record already exists' })
            } else {
              formik.setStatus({ errorMessage: e.message })
            }
          })
          .finally(() => {
            setLoading(false)
          })
      },
    })

    const closeModal = () => setModalOpen(false)

    useEffect(() => {
      document.body.style.overflow = isModalOpen ? 'hidden' : 'auto'
      return () => {
        document.body.style.overflow = 'auto'
      }
    }, [isModalOpen])

    return (
      <Flex
        bg="white"
        h="100vh"
        flexDir="column"
        p={{ md: '10', base: '0' }}
        fontSize={{ md: '1.125rem', base: '1rem' }}
      >
        <Metadata {...waitlistMetadata} />
      <Link href="/">
        <Button
          leftIcon={<ChevronLeftIcon />}
          w="fit-content"
          bg="transparent"
          color="#ADB5BD"
          _hover={{
            bg: 'transparent',
          }}
        >
          Go back
        </Button>
      </Link>

      <section className="mx-auto mt-16 flex flex-col items-center">
        <div className=" mb-[60px] md:mb-[120px] items-center justify-center flex flex-col">
          <Box p={{ base: '5', md: '7' }} rounded="full">
            <Link href="/">
              <Image src={BlackLogo} alignSelf="center" />
            </Link>
          </Box>
          <div className="flex flex-col items-center justify-center space-y-2.5">
            <p className="text-[#ADB5BD] text-[13px] md:text-lg leading-6 md:leading-7">
              Be the first to know
            </p>
            <p className="text-black text-[22px] md:text-[28px] leading-[26px] md:leading-10 pt-2.5 font-semibold md:font-normal text-center">
              Join the waitlist for Safelto as a community manager
            </p>
          </div>
        </div>
        <div>
          <VStack spacing={8} mt={'10'} w={{ base: '340px', md: '474px', lg: '474px' }}>
            <FormControl>
              <FormInput
                placeholder="Enter your full name"
                variant="outline"
                value={formik.values.name}
                onChange={formik.handleChange}
                name="name"
                error={formik.errors.name}
                size="lg"
              />
            </FormControl>
            <FormControl>
              <FormInput
                placeholder="Enter your email address"
                variant="outline"
                value={formik.values.email}
                onChange={formik.handleChange}
                name="email"
                error={formik.errors.email}
                size="lg"
              />
            </FormControl>
          </VStack>
          <VStack mt={'70px'}>
            <DefaultButton
              isLoading={loading}
              loadingText="Submitting..."
              onClick={() => formik.handleSubmit()}
              w={{ base: '340px', md: '474px', lg: '474px' }}
              background="#E9ECEF"
              borderColor="#E9ECEF"
              marginBottom="20"
              color="#FFFFFF"
              size="lg"
              px="2.5"
              _hover={{
                background: 'black',
                color: 'white',
                border: 'none',
              }}
              isDisabled={loading || !formik.isValid || !formik.dirty}
            >
              Join waitlist
            </DefaultButton>
          </VStack>
        </div>
      </section>

      {isModalOpen && (
        <div className="fixed inset-0 bg-white bg-opacity-50 flex justify-center z-50">
          <div className=" mt-[214px] md:mt-[290px] bg-white text-black w-[px] md:w-[575px] h-[400px] md:h-[440px] rounded-lg p-5 md:p-6 text-center drop-shadow-2xl">
            <header className=" text-xl md:text-[28px] leading-10 mb-3 md:mb-[30px] tracking-wide md:tracking-wider">
              You have been added to the <br />
              waitlist
            </header>
            <section className="mb-4">
              <p className="font-normal md:font-medium mb-[63px] md:mt-[30px] text-[13px] md:text-normal md:leading-6">
                You’d be the first to know once we start operating.
              </p>
              <div className="border border-[#CED4DA] rounded-lg p-4 mb-[99px] text-left">
                {submittedEmail}
              </div>
            </section>
            <footer className="flex justify-end">
              <DefaultButton
                onClick={closeModal}
                background="#FFF"
                color="#ADB5BD"
                _hover={{
                  background: 'white',
                  color: '#495057',
                  border: 'none',
                }}
              >
                Close
              </DefaultButton>
            </footer>
          </div>
        </div>
      )}
    </Flex>
  )
}

export default WaitingListPage
